/**
 * Fresh Harvest — Matter.js fruit physics.
 *
 * Fruit spawn in random positions above the scene, fall under gravity, and
 * collide with both the walls and the "Fresh Harvest" title so they pile up
 * on top of the text. Drag with the mouse to throw them around.
 */
export default class FreshHarvest {
    constructor(root) {
        this.root = root;
        this.canvasHost = root.querySelector('.fresh-harvest__fruits');

        if (!this.canvasHost || typeof window === 'undefined') {
            return;
        }

        // Respect reduced-motion preferences — skip the physics scene
        // entirely and leave the static title in place.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const Matter = window.Matter;
        if (!Matter) {
            console.warn('FreshHarvest: Matter.js is not loaded.');
            return;
        }

        try {
            this.textures = JSON.parse(this.root.dataset.freshHarvestTextures || '[]');
        } catch (e) {
            this.textures = [];
        }

        if (this.textures.length === 0) {
            return;
        }

        this.Matter = Matter;
        this.wallThickness = 50.5;
        // Desktop reference radius, tuned for the 1380px max-width scene.
        // The actual radius scales with the scene width (see radiusForWidth)
        // so the fruit stay proportional instead of ballooning on mobile.
        this.baseRadius = 69;
        this.referenceWidth = 1380;
        this.minRadius = 22;
        this.fruitCount = 14;

        // On mobile the fruit read better a touch larger, with fewer of them
        // so the pile doesn't crowd the shorter scene.
        this.mobileBreakpoint = 768;
        this.mobileRadiusScale = 1.5625;
        this.desktopRadiusScale = 1.25;
        this.mobileCountScale = 0.75;

        // Sprite scales are relative to each texture's native pixel size, so
        // measure the images first and start once the sizes are known.
        this.textureSizes = {};
        this.preloadTextures().then(() => this.start());
    }

    preloadTextures() {
        return Promise.all(
            this.textures.map(
                (src) =>
                    new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => {
                            this.textureSizes[src] = {
                                width: img.naturalWidth,
                                height: img.naturalHeight,
                            };
                            resolve();
                        };
                        img.onerror = () => resolve();
                        img.src = src;
                    })
            )
        );
    }

    start() {
        const { Engine, Render, Runner, Bodies, Body, Composite, Mouse, MouseConstraint } = this.Matter;

        const bounds = this.canvasHost.getBoundingClientRect();
        const width = Math.max(bounds.width, 1);
        const height = Math.max(bounds.height, 1);

        this.engine = Engine.create();
        this.world = this.engine.world;

        this.render = Render.create({
            element: this.canvasHost,
            engine: this.engine,
            options: {
                width,
                height,
                background: 'transparent',
                wireframes: false,
                showAngleIndicator: false,
                pixelRatio: window.devicePixelRatio || 1,
            },
        });

        Render.run(this.render);

        this.runner = Runner.create();
        Runner.run(this.runner, this.engine);

        this.staticOptions = { isStatic: true, render: { visible: false } };
        this.buildWalls(width, height);

        this.fruits = [];
        this.spawnFruits();

        const mouse = Mouse.create(this.render.canvas);

        // Let wheel/trackpad scroll pass through. Matter attaches a wheel
        // listener that calls preventDefault, so we intercept at capture
        // phase and stop it from ever reaching Matter's handler.
        const canvasEl = this.render.canvas;
        ['wheel', 'mousewheel', 'DOMMouseScroll'].forEach((evt) => {
            canvasEl.addEventListener(evt, (e) => {
                e.stopImmediatePropagation();
            }, { capture: true, passive: true });
        });

        // Touch scroll is handled by `touch-action: pan-y` in CSS — the
        // browser owns vertical pan regardless of any JS preventDefault.

        const mouseConstraint = MouseConstraint.create(this.engine, {
            mouse,
            constraint: {
                stiffness: 0.2,
                render: { visible: false },
            },
        });
        Composite.add(this.world, mouseConstraint);
        this.render.mouse = mouse;

        Render.lookAt(this.render, {
            min: { x: 0, y: 0 },
            max: { x: width, y: height },
        });

        this.onResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.onResize);

        this.lastScrollY = window.scrollY;
        // Armed only when the canvas is fully below the viewport, so the
        // trigger fires once per approach from above.
        this.spawnArmed = this.canvasHost.getBoundingClientRect().top > window.innerHeight;
        this.onScroll = this.handleScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    spawnFruits() {
        const { Bodies, Body, Composite } = this.Matter;

        if (this.fruits && this.fruits.length) {
            this.fruits.forEach((body) => Composite.remove(this.world, body));
        }
        this.fruits = [];

        const bounds = this.canvasHost.getBoundingClientRect();
        const width = Math.max(bounds.width, 1);
        const height = Math.max(bounds.height, 1);
        const baseRadius = this.radiusForWidth(width);
        const spawnBandHeight = Math.max(height * 0.35, baseRadius * 2);
        const count = this.isMobile()
            ? Math.round(this.fruitCount * this.mobileCountScale)
            : this.fruitCount;

        for (let i = 0; i < count; i++) {
            const scale = 0.75 + Math.random() * 0.4;
            const radius = baseRadius * scale;
            const x = radius + Math.random() * Math.max(width - radius * 2, 1);
            const y = radius + Math.random() * spawnBandHeight;

            const texture = this.pickTexture();

            // Normalize the sprite to the body's diameter regardless of the
            // source image's pixel dimensions.
            const size = this.textureSizes[texture] || {};
            const naturalSize = Math.max(size.width || 0, size.height || 0) || radius * 2;
            const spriteScale = (radius * 2) / naturalSize;

            const body = Bodies.circle(x, y, radius, {
                density: 0.0005,
                frictionAir: 0.06,
                restitution: 0.3,
                friction: 0.01,
                angle: Math.random() * Math.PI * 2,
                render: {
                    sprite: {
                        texture,
                        xScale: spriteScale,
                        yScale: spriteScale,
                    },
                },
            });

            Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.05);
            Body.setVelocity(body, { x: (Math.random() - 0.5) * 1.5, y: 0 });
            Composite.add(this.world, body);
            this.fruits.push(body);
        }
    }

    isMobile() {
        return window.innerWidth < this.mobileBreakpoint;
    }

    // Scale the fruit radius to the scene width so they stay proportional.
    // 69px was tuned for the 1380px desktop scene; on a ~470px phone that
    // ratio gives ~23px, instead of gigantic fruit that bury the title. A
    // per-breakpoint bump then sizes the fruit up (desktop ×1.25, mobile more)
    // so the pile reads well at both ends.
    radiusForWidth(width) {
        const scaled = width * (this.baseRadius / this.referenceWidth);
        const clamped = Math.max(this.minRadius, Math.min(this.baseRadius, scaled));
        return clamped * (this.isMobile() ? this.mobileRadiusScale : this.desktopRadiusScale);
    }

    handleScroll() {
        const currentY = window.scrollY;
        const scrollingDown = currentY > this.lastScrollY;
        this.lastScrollY = currentY;

        const rect = this.canvasHost.getBoundingClientRect();

        // Re-arm when the canvas drops back below the viewport so the
        // trigger can fire again next time the user scrolls down to it.
        if (rect.top > window.innerHeight) {
            this.spawnArmed = true;
            return;
        }

        if (scrollingDown && this.spawnArmed && rect.top <= window.innerHeight) {
            this.spawnArmed = false;
            this.spawnFruits();
        }
    }

    pickTexture() {
        const i = Math.floor(Math.random() * this.textures.length);
        return this.textures[i];
    }

    buildWalls(width, height) {
        const { Bodies, Composite } = this.Matter;
        const t = this.wallThickness;
        const half = t / 2;

        if (this.walls) {
            Composite.remove(this.world, this.walls);
        }

        this.walls = [
            Bodies.rectangle(width / 2, -half, width, t, this.staticOptions),
            Bodies.rectangle(width / 2, height + half, width, t, this.staticOptions),
            Bodies.rectangle(-half, height / 2, t, height, this.staticOptions),
            Bodies.rectangle(width + half, height / 2, t, height, this.staticOptions),
        ];

        Composite.add(this.world, this.walls);
    }

    handleResize() {
        const { Render } = this.Matter;
        const bounds = this.canvasHost.getBoundingClientRect();
        const width = Math.max(bounds.width, 1);
        const height = Math.max(bounds.height, 1);

        // iOS Safari fires resize whenever the URL bar collapses/expands
        // while scrolling. The scene box is width-driven (aspect-ratio), so
        // its rect is unchanged — bail before touching the canvas or
        // re-dropping the fruit pile.
        if (width === this.render.options.width && height === this.render.options.height) {
            return;
        }

        this.render.options.width = width;
        this.render.options.height = height;
        // Resize through Matter so the backing store stays at
        // width × pixelRatio — writing canvas.width directly drops the
        // retina scaling and everything renders devicePixelRatio× too large.
        Render.setPixelRatio(this.render, window.devicePixelRatio || 1);

        this.buildWalls(width, height);

        Render.lookAt(this.render, {
            min: { x: 0, y: 0 },
            max: { x: width, y: height },
        });

        // Re-spawn (debounced) so the fruit re-scale to the new width — a
        // desktop-to-mobile resize would otherwise leave them oversized.
        clearTimeout(this.resizeRespawnTimer);
        this.resizeRespawnTimer = setTimeout(() => this.spawnFruits(), 250);
    }
}
